import {
  ChangeDetectorRef,
  Directive,
  Input,
  OnChanges,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { ReplaySubject, Subject, combineLatest } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { PermissionService } from './permission.service';
import { PermissionId } from './permission.types';

type PermissionMatchMode = 'any' | 'all';

interface PermissionInputs {
  permissions: readonly PermissionId[];
  mode: PermissionMatchMode;
}

/**
 * Structural directive that renders its template only when the current
 * user holds the supplied permission(s).
 *
 * Usage:
 * ```html
 * <button *hasPermission="'transaction.create'">…</button>
 *
 * <button
 *   *hasPermission="['transaction.broadcast', 'transaction.sign']; mode: 'any'"
 * >…</button>
 * ```
 *
 * Pass an `else` template to render an alternate view when denied:
 * ```html
 * <button *hasPermission="'vault.create'; else readOnly">Create vault</button>
 * <ng-template #readOnly><span class="text-input">Read-only</span></ng-template>
 * ```
 *
 * Defaults: a single id is matched directly; an array uses `mode: 'any'`
 * (the user needs **at least one** permission). Use `mode: 'all'` to
 * require every permission in the list.
 *
 * The check runs in `ngOnChanges`, after Angular has set *all* the inputs:
 * microsyntax binds `hasPermission` before `mode` and `else`, so checking
 * from the first setter ignored both (F-022 showed an `all` control to a
 * holder of one permission; F-002 never rendered the else template).
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector -- `*hasPermission` is the structural directive's public API across the app's templates; renaming is a breaking sweep for another day
  selector: '[hasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnChanges, OnDestroy {
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly permissions = inject(PermissionService);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly inputs$ = new ReplaySubject<PermissionInputs>(1);
  private readonly destroy$ = new Subject<void>();

  private currentPermissions: readonly PermissionId[] = [];
  private currentMode: PermissionMatchMode = 'any';
  private elseTemplateRef: TemplateRef<unknown> | null = null;
  private shown: TemplateRef<unknown> | null = null;

  constructor() {
    this.inputs$
      .pipe(
        switchMap(({ permissions, mode }) =>
          combineLatest([
            mode === 'all'
              ? this.permissions.canAll$(permissions)
              : this.permissions.canAny$(permissions),
          ])
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([allowed]) => this.render(allowed));
  }

  @Input()
  set hasPermission(
    value: PermissionId | readonly PermissionId[] | null | undefined
  ) {
    this.currentPermissions =
      value == null ? [] : Array.isArray(value) ? value : [value];
  }

  @Input()
  set hasPermissionMode(value: PermissionMatchMode | null | undefined) {
    this.currentMode = value === 'all' ? 'all' : 'any';
  }

  @Input()
  set hasPermissionElse(value: TemplateRef<unknown> | null | undefined) {
    this.elseTemplateRef = value ?? null;
  }

  ngOnChanges(): void {
    this.inputs$.next({
      permissions: this.currentPermissions,
      mode: this.currentMode,
    });
  }

  private render(allowed: boolean): void {
    const target = allowed ? this.templateRef : this.elseTemplateRef;
    if (target === this.shown) {
      return;
    }

    this.viewContainer.clear();
    if (target) {
      this.viewContainer.createEmbeddedView(target);
    }
    this.shown = target;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.inputs$.complete();
  }
}
